import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { RenderingProvider, useCms, useContent } from '@eneco/cms';
import env from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import { dispatchCookieConsentChange } from '@eneco/hooks/cookieConsent';
import { AlertDialog, Button, Stack, TextLink } from '@eneco/sparky';
// eslint-disable-next-line @nx/enforce-module-boundaries
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import type { CookieWallContent, CookieWallPageContent } from './cookie-wall-content.types';
import { isEmbeddedByStudio } from './is-embedded-by-studio';

const COOKIE_VERSIONS = {
  QUBIT: '2.0.0',
  PRIVACY: '1',
} as const;

/** Screen-reader name for this overlay until Sitecore exposes a dedicated field. */
const COOKIE_WALL_SR_LABEL = 'Cookiemelding';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CookieWallLinkReplacement: FC<any> = props => <TextLink {...props} />;

/**
 * After the wall closes on its own, move focus to the page content.
 * The skip link stays off-screen until it is focused, so focusing it slides that
 * control into view. `main` is not in the tab order, so the keyboard position stays
 * at the start of the page without showing a control.
 */
const focusPageStart = (): void => {
  const main = document.getElementById('main');
  if (!(main instanceof HTMLElement)) return;

  if (!main.hasAttribute('tabindex')) {
    main.tabIndex = -1;
  }

  main.focus({ preventScroll: true });
};

/**
 * What the component registry spreads onto CookieWall when the CMS places it on
 * a page. Both lanes supply these: Sitecore's placeholder spreads the raw
 * rendering, and the `@eneco/cms` placeholder passes already-flat content as
 * `fields` for exactly this reason. `unwrapFields` is idempotent, so the same
 * bridge handles either shape.
 */
type CookieWallCmsProps = {
  fields?: unknown;
  params?: unknown;
  componentName?: string;
  uid?: string;
};

/**
 * Bridges the rendering into the CMS-agnostic context so the dialog below can
 * read it with `useContent`. Needed because the Sitecore placeholder populates
 * only its own rendering context, not this one.
 */
const CookieWall: FC<CookieWallCmsProps> = ({ fields, params, componentName, uid }) => {
  const { adapter } = useCms();
  const rendering = useMemo(
    () => adapter.toRendering({ componentName: componentName ?? 'CookieWall', uid, fields, params }),
    [adapter, componentName, uid, fields, params],
  );

  return (
    <RenderingProvider rendering={rendering}>
      <CookieWallDialog />
    </RenderingProvider>
  );
};

const CookieWallDialog: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const acceptButtonRef = useRef<HTMLButtonElement>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const router = useRouter();
  const { title, description, yesButtonText, noButtonText } = useContent<CookieWallContent>();
  const { page } = useCms();
  const hideCookieWall = Boolean((page.root.content as CookieWallPageContent).hideCookieWall);

  const handleSetCookie = (accept: boolean) => {
    const qubitValue = accept ? 'Accepted' : 'Declined';
    const privacyValue = accept ? 'true' : 'false';
    const host = env('HOST');
    const domain = host ? new URL(host).hostname.replace(/^www\./, '') : undefined;
    const cookieOptions: Cookies.CookieAttributes = { expires: 365, ...(domain && { domain }) };
    Cookies.set('qubitconsent', qubitValue, cookieOptions);
    Cookies.set('qubitconsent_version', COOKIE_VERSIONS.QUBIT, cookieOptions);
    Cookies.set('privacyConsent', privacyValue, cookieOptions);
    Cookies.set('privacyConsent_type', 'explicit', cookieOptions);
    Cookies.set('privacyConsent_version', COOKIE_VERSIONS.PRIVACY, cookieOptions);
    dispatchCookieConsentChange();
  };

  useEffect(() => {
    const checkCookiesAndAttachListeners = () => {
      const cookiesExists =
        Cookies.get('qubitconsent') && Cookies.get('privacyConsent') && Cookies.get('privacyConsent_type');
      const cookiesVersionMisMatch =
        Cookies.get('qubitconsent_version') !== COOKIE_VERSIONS.QUBIT ||
        Cookies.get('privacyConsent_version') !== COOKIE_VERSIONS.PRIVACY;

      // In the Studio's preview the wall only covers the page being edited.
      const isStudioPreview = isEmbeddedByStudio(env('SANITY_STUDIO_URL'));

      if ((!cookiesExists || cookiesVersionMisMatch) && !hideCookieWall && !isStudioPreview) {
        setIsOpen(true);
      }

      const handleClick = (event: Event) => {
        event.preventDefault();
        lastTriggerRef.current = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
        setIsOpen(true);
      };

      // Find all anchor elements that have '#cookiepermissionmodal' in their href attribute
      const elements = Array.from(document.querySelectorAll('a[href*="#cookiepermissionmodal"]')) as HTMLElement[];
      elements.forEach(element => {
        element.addEventListener('click', handleClick);
      });

      // Cleanup event listener on component unmount
      return () => {
        elements.forEach(element => {
          element.removeEventListener('click', handleClick);
        });
      };
    };

    const cleanup = checkCookiesAndAttachListeners();
    router.events?.on('routeChangeComplete', checkCookiesAndAttachListeners);

    return () => {
      cleanup();
      router.events?.off('routeChangeComplete', checkCookiesAndAttachListeners);
    };
  }, [router]);

  const footer = (
    <Stack direction={{ initial: 'column', md: 'row' }} gap="5">
      <AlertDialogPrimitive.Action asChild>
        <Button ref={acceptButtonRef} onClick={() => handleSetCookie(true)}>
          {yesButtonText}
        </Button>
      </AlertDialogPrimitive.Action>
      <AlertDialogPrimitive.Action asChild>
        <Button onClick={() => handleSetCookie(false)}>{noButtonText}</Button>
      </AlertDialogPrimitive.Action>
    </Stack>
  );

  return (
    <AlertDialog
      isOpen={isOpen}
      setOpen={setIsOpen}
      title={title}
      accessibleName={`${COOKIE_WALL_SR_LABEL}. ${title}`}
      description={
        <RichText
          html={description}
          replacements={{
            a: CookieWallLinkReplacement,
          }}
        />
      }
      footer={footer}
      onOpenAutoFocus={event => {
        event.preventDefault();
        acceptButtonRef.current?.focus();
      }}
      onCloseAutoFocus={event => {
        event.preventDefault();
        const trigger = lastTriggerRef.current;
        lastTriggerRef.current = null;
        if (trigger?.isConnected) {
          trigger.focus();
          return;
        }
        focusPageStart();
      }}
    />
  );
};

export default CookieWall;
